import t from 'react-translate';
import { BadgeColor } from 'shared/components/nv-pill-tab';
import { LearnerProgressSavedView } from 'redux/schemas/models/learner-progress-saved-view';
import { CourseAliases } from 'redux/schemas/models/course';
import { config } from '../../../config/pendo.config.json';
import { TabTypes, LearnerProgressTotals } from './learner-progress-tabs';

enum TabItemTypes {
  TAB = 'tab',
  SAVED_VIEW = 'savedView',
}

export type PillTabItem = {
  type: TabItemTypes.TAB;
  id: string;
  text: string;
  count: number;
  onClick: () => void;
  dataQA: string;
  badgeColor: BadgeColor;
};

export type SavedViewTabItem = {
  type: TabItemTypes.SAVED_VIEW;
  id: string;
  view: LearnerProgressSavedView;
};

export type TabItem = PillTabItem | SavedViewTabItem;

type BuildDefaultTabsParams = {
  aliases: CourseAliases;
  totals: LearnerProgressTotals;
  onSelectTab: (tab: TabTypes) => void;
};

/* @ngInject */
export function buildDefaultTabs({
  aliases,
  totals,
  onSelectTab,
}: BuildDefaultTabsParams): PillTabItem[] {
  return [
    {
      type: TabItemTypes.TAB,
      text: t.LEARNER_PROGRESS.TABS.TOTAL_LEARNERS({ ...aliases.learnersAliases }),
      count: totals.totalLearners,
      onClick: () => onSelectTab(TabTypes.TOTAL),
      id: TabTypes.TOTAL,
      dataQA: config.pendo.learnerProgress.tabs.totalLearers,
      badgeColor: BadgeColor.PRIMARY,
    },
    {
      type: TabItemTypes.TAB,
      text: t.LEARNER_PROGRESS.TABS.COMPLETED(),
      count: totals.completed,
      onClick: () => onSelectTab(TabTypes.COMPLETED),
      id: TabTypes.COMPLETED,
      dataQA: config.pendo.learnerProgress.tabs.completed,
      badgeColor: BadgeColor.SUCCESS,
    },
    {
      type: TabItemTypes.TAB,
      text: t.LEARNER_PROGRESS.TABS.IN_PROGRESS(),
      count: totals.inProgress,
      onClick: () => onSelectTab(TabTypes.IN_PROGRESS),
      id: TabTypes.IN_PROGRESS,
      dataQA: config.pendo.learnerProgress.tabs.inProgress,
      badgeColor: BadgeColor.PRIMARY,
    },
    {
      type: TabItemTypes.TAB,
      text: t.LEARNER_PROGRESS.TABS.MISSED_DEADLINES(),
      count: totals.missedDeadlines,
      onClick: () => onSelectTab(TabTypes.MISSED_DEADLINE),
      id: TabTypes.MISSED_DEADLINE,
      dataQA: config.pendo.learnerProgress.tabs.missedDeadlines,
      badgeColor: BadgeColor.DANGER,
    },
    {
      type: TabItemTypes.TAB,
      text: t.LEARNER_PROGRESS.TABS.NEVER_ACTIVE(),
      count: totals.neverActive,
      onClick: () => onSelectTab(TabTypes.NEVER_ACTIVE),
      id: TabTypes.NEVER_ACTIVE,
      dataQA: config.pendo.learnerProgress.tabs.neverActive,
      badgeColor: BadgeColor.DANGER,
    },
  ];
}

/**
 * Builds the ordered list of tab items:
 * 1. First tab (Total Learners)
 * 2. Saved views
 * 3. Remaining tabs
 */
/* @ngInject */
export function buildTabItems(
  tabs: PillTabItem[],
  savedViews: LearnerProgressSavedView[],
): TabItem[] {
  const [firstTab, ...restTabs] = tabs;

  const reversedSavedViews = [...savedViews].reverse();

  return [
    firstTab,
    ...reversedSavedViews.map((view): SavedViewTabItem => ({
      type: TabItemTypes.SAVED_VIEW,
      id: `saved_${view.viewCode}`,
      view,
    })),
    ...restTabs,
  ];
}
